import { useCallback, useRef, useState } from 'react';
import { Barcode } from 'lucide-react';
import { classifyPosScanPayload, POS_SCAN_KIND } from '../../utils/posScanCode';
import { messageForNonRetailScanKind } from '../../utils/posBarcodeCaisseMessages';

/**
 * Saisie manuelle permanente — GTIN fabricant ou code interne CLKI (même moteur que la douchette).
 */
export default function PosBarcodeManualEntry({ disabled, onSubmitGtin, onValidationError }) {
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  const submit = useCallback(async () => {
    if (disabled) return;
    const raw = value.trim();
    if (!raw) return;

    const classified = classifyPosScanPayload(raw);
    let scannableCode = null;
    if (classified.kind === POS_SCAN_KIND.RETAIL_GTIN && classified.gtin) {
      scannableCode = classified.gtin;
    } else if (classified.kind === POS_SCAN_KIND.CLICKORICO_INTERNAL && classified.internalCode) {
      scannableCode = classified.internalCode;
    } else {
      onValidationError?.(messageForNonRetailScanKind(classified.kind));
      return;
    }

    const ok = await onSubmitGtin(scannableCode);
    if (ok) {
      setValue('');
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [disabled, onSubmitGtin, onValidationError, value]);

  return (
    <form
      className="mt-2 flex gap-2 items-stretch"
      data-testid="pos-barcode-manual-form"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className="relative flex-1 min-w-0">
        <Barcode
          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
          aria-hidden
        />
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          enterKeyHint="done"
          data-testid="pos-barcode-manual-input"
          aria-label="Saisir ou scanner un code-barres"
          placeholder="Saisir ou scanner un code-barres"
          disabled={disabled}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 bg-white focus:border-emerald-500 outline-none text-sm font-mono tracking-wide disabled:opacity-50"
        />
      </div>
      <button
        type="submit"
        data-testid="pos-barcode-manual-submit"
        disabled={disabled || !value.trim()}
        className="shrink-0 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Valider
      </button>
    </form>
  );
}
